import { useEffect, useState } from 'react';
import {
  Flex,
  Text,
  Separator,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Icon,
} from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';
import type { IWebhookConfigurationProps } from './types';
import { ConfigFormRow } from './ConfigFormRow';
import { ConfigInput } from './ConfigInput';
import { getDefaultValue, isOptionalField } from './utils';

/**
 * WebhookConfiguration Component
 *
 * Renders a dynamic configuration form based on the selected trigger's
 * configuration schema. Supports text, number, boolean, enum, and
 * sensitive (password) input types.
 *
 * The configuration is displayed in a collapsible panel that can be
 * expanded/collapsed by clicking the header.
 *
 * @param props - Component props
 * @returns JSX element containing the configuration form
 */
export function WebhookConfiguration({
  configuration,
  values,
  onValueChange,
  readOnly = false,
  errors,
  highlightedKey,
  highlightNonce,
}: IWebhookConfigurationProps) {
  const { t } = useTranslation('automation-inputs');
  // Start expanded if any field is required so the user sees what they need to
  // fill out (and the required asterisks) without having to expand the panel
  // first.
  const [isOpen, setIsOpen] = useState(() =>
    Object.values(configuration).some(
      descriptor => !isOptionalField(descriptor),
    ),
  );

  // Reset the open state whenever the configuration schema changes — i.e. the
  // user switched to a different event. Without this, switching from one
  // event to another (when both have configuration) keeps the same component
  // instance and the open state would stay frozen on whatever the user last
  // chose. The effect is keyed on `configuration` identity, which only
  // changes when the parent passes a new schema, so manual collapses on the
  // same event still persist across unrelated re-renders.
  // eslint-disable-next-line no-restricted-syntax -- reason: derive open state from external schema changes
  useEffect(() => {
    setIsOpen(
      Object.values(configuration).some(
        descriptor => !isOptionalField(descriptor),
      ),
    );
  }, [configuration]);

  // Expand the panel when the assistant fills a field while it's collapsed, so
  // the user sees what was filled (mirrors the Filters section behaviour).
  // eslint-disable-next-line no-restricted-syntax -- reason: reveal the panel when a field is auto-filled
  useEffect(() => {
    if (highlightedKey) setIsOpen(true);
  }, [highlightedKey]);

  const configKeys = Object.keys(configuration);

  if (configKeys.length === 0) {
    return null;
  }

  return (
    <>
      {/* Separator between event select and configuration */}
      <Separator className="my-2" />

      <Collapsible
        open={isOpen}
        onOpenChange={setIsOpen}
        className="w-full bg-secondary rounded-lg p-5"
      >
        {/* Collapsible Header */}
        <CollapsibleTrigger className="w-full">
          <Flex
            direction="row"
            align="start"
            justify="between"
            className="px-2.5 pt-2 pb-2 cursor-pointer hover:bg-accent/50 rounded-md transition-colors"
          >
            <Flex direction="column" gap={1} align="start">
              <Text className="font-medium text-md">
                {t('webhookDialog.configuration.title')}
              </Text>
            </Flex>
            <Icon
              type={isOpen ? 'ChevronUp' : 'ChevronDown'}
              size="sm"
              className="text-muted-foreground shrink-0 mt-0.5"
            />
          </Flex>
        </CollapsibleTrigger>

        {/* Collapsible Content - Configuration Fields */}
        <CollapsibleContent className="pt-2">
          <Flex direction="column" gap={0}>
            {configKeys.map(key => {
              const descriptor = configuration[key];
              const defaultValue = getDefaultValue(descriptor);
              const currentValue = values[key] ?? defaultValue;

              return (
                <ConfigFormRow
                  key={key}
                  label={key}
                  description={descriptor.description}
                  error={errors?.[key]}
                  highlighted={highlightedKey === key}
                  highlightNonce={highlightNonce}
                >
                  <ConfigInput
                    descriptor={descriptor}
                    value={currentValue}
                    onChange={v => onValueChange(key, v)}
                    readOnly={readOnly}
                  />
                </ConfigFormRow>
              );
            })}
          </Flex>
        </CollapsibleContent>
      </Collapsible>
    </>
  );
}
